import { useState } from 'react';
import { C } from '@ridgewill/brand';
import { Select } from '../components/Select.js';
import { adminApi, type AppRoleRow } from '../lib/api.js';
import { useAsync, useAction } from '../lib/useAsync.js';
import { count } from '../lib/format.js';
import { InfoBubble, inputStyle } from '../components/Screen.js';
import { useCan, whyDisabled } from '../lib/capabilities.js';
import {
  AsyncBoundary,
  Button,
  EmptyState,
  Field,
  HoldButton,
  Note,
  OutcomeNote,
  Panel,
  PanelBody,
  Table,
  Td,
} from '../components/ui.js';
import { Modal, ModalActions } from '../components/Modal.js';

/**
 * The roles one application defines — and which this platform cannot interpret.
 *
 * Auth stores the NAME and who holds it. What `App Admin` permits inside Keyodo
 * is Keyodo's business, and auth is in no position to review a meaning it does
 * not hold. That is the whole difference from platform roles, which live in
 * TypeScript precisely because they confer privilege over the auth service and
 * are reviewed in a pull request.
 *
 * ── THE CREATE FORM IS THE ONLY TYPO GUARD THERE IS ──────────────────
 *
 * A role created as `App Admn` assigns cleanly, lands in a token, and does
 * nothing at the far end: no error, no permission, and nothing on the row to say
 * it is misspelled rather than new. Nothing downstream can catch it, because
 * downstream is a different codebase that has never heard of it.
 *
 * So the form shows the exact string a consumer will match on, in monospace,
 * separately from the label staff read — and says out loud that it cannot be
 * changed afterwards.
 */
export function AppRoles({ applicationId, appSlug }: { applicationId: string; appSlug: string }) {
  const [adding, setAdding] = useState(false);
  const { state, reload } = useAsync(() => adminApi.appRoles(applicationId), [applicationId]);
  const action = useAction();
  const mayWrite = useCan('application:write');

  return (
    <Panel
      title="App roles"
      /* The caveat moved off the panel and into its header (org-admin#56). It
         has to be said — nothing on this platform can tell a typo from a new
         role — but a paragraph standing over every visit stops being read after
         the second one, and it was standing over a two-row table. */
      info={
        <InfoBubble label="What an app role is">
          Auth stores the name. <strong>{appSlug}</strong> decides what it means &mdash; nothing here
          can check it, or tell a typo from a new role.
        </InfoBubble>
      }
      action={
        <Button
          disabled={action.running || !mayWrite}
          title={mayWrite ? undefined : whyDisabled('creating app roles')}
          onClick={() => setAdding(true)}
        >
          + Create role
        </Button>
      }
    >
      {action.outcome ? (
        <PanelBody>
          <OutcomeNote outcome={action.outcome} />
        </PanelBody>
      ) : null}

      <AsyncBoundary state={state}>
        {(d) =>
          d.roles.length === 0 ? (
            <EmptyState
              title="No app roles"
              suggestion="Create one to give people a name this application will recognise in their token."
            />
          ) : (
            <Table columns={['Role', 'Held by', '']} actionsLast>
              {d.roles.map((role) => (
                <tr key={role.id} className="rw-rowhover">
                  <Td>
                    <div style={{ fontWeight: 600 }}>{role.label}</div>
                    {/* The value a consumer matches on, in monospace and always
                        visible. The label above it can be edited freely; this
                        cannot be edited at all. */}
                    <code style={{ fontSize: '.78rem', color: C.muted }}>{role.name}</code>
                    {role.description ? (
                      <div style={{ fontSize: '.78rem', color: C.muted }}>{role.description}</div>
                    ) : null}
                  </Td>
                  <Td>
                    <Assigned role={role} />
                  </Td>
                  <Td action>
                    {/*
                      A HOLD, like the members table on the Security groups
                      screen and for the same reason: this row sits in a list,
                      the action is irreversible, and it takes every assignment
                      with it. Unlike that one, deleting also changes what a
                      DIFFERENT codebase will do — the name stops arriving in
                      tokens and the app starts refusing whatever it gated.
                    */}
                    <HoldButton
                      disabled={action.running}
                      title={`Hold for a second and a half to delete ${role.name} and every assignment of it.`}
                      onHold={() =>
                        void action
                          .run(async () => {
                            const r = await adminApi.deleteAppRole(role.id);
                            return r.users + r.groups === 0
                              ? `${role.name} deleted. Nobody held it.`
                              : `${role.name} deleted, from ${count(r.users)} ${
                                  r.users === 1 ? 'account' : 'accounts'
                                } and ${count(r.groups)} ${r.groups === 1 ? 'group' : 'groups'}.`;
                          })
                          .then(() => reload())
                      }
                    >
                      Delete
                    </HoldButton>
                  </Td>
                </tr>
              ))}
            </Table>
          )
        }
      </AsyncBoundary>

      {adding ? (
        <CreateAppRole
          appSlug={appSlug}
          applicationId={applicationId}
          onClose={() => setAdding(false)}
          onCreated={() => {
            setAdding(false);
            reload();
          }}
        />
      ) : null}
    </Panel>
  );
}

/** "1 person, 0 groups" — both, not a total. A group's count moves when nobody
 *  touched this role, and the two are answered by different screens. */
function Assigned({ role }: { role: AppRoleRow }) {
  if (role.users === 0 && role.groups === 0) {
    return <span style={{ color: C.muted }}>nobody</span>;
  }
  return (
    <span style={{ fontSize: '.84rem' }}>
      {count(role.users)} {role.users === 1 ? 'person' : 'people'}, {count(role.groups)}{' '}
      {role.groups === 1 ? 'group' : 'groups'}
    </span>
  );
}

function CreateAppRole({
  applicationId,
  appSlug,
  onClose,
  onCreated,
}: {
  applicationId: string;
  appSlug: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const action = useAction();

  return (
    <Modal
      title={`Create a role for ${appSlug}`}
      onClose={onClose}
      footer={
        <ModalActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            disabled={action.running || !name.trim() || !label.trim()}
            onClick={() =>
              void action
                .run(async () => {
                  await adminApi.createAppRole(applicationId, {
                    name: name.trim(),
                    label: label.trim(),
                    description: description.trim() || undefined,
                  });
                  return 'Created.';
                })
                .then(onCreated)
            }
          >
            {action.running ? 'Creating…' : 'Create'}
          </Button>
        </ModalActions>
      }
    >
      <OutcomeNote outcome={action.outcome} />
      <Note tone="warn">
        The name below is compared, character for character, by {appSlug}. Nothing on this platform
        can check it — a misspelled role assigns cleanly and does nothing.
      </Note>
      <Field
        label="Name"
        hint="PERMANENT, and the exact string the application matches on. There is no rename: it is compared as bare text with no foreign key, so changing it would revoke the role inside the app while leaving every assignment standing here."
      >
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{ ...inputStyle, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
          placeholder="app-admin"
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
        />
      </Field>
      <Field label="Label" hint="What staff see in this portal. Editable, and matched on by nobody.">
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          style={inputStyle}
          placeholder="App Admin"
        />
      </Field>
      <Field label="Description" hint="What this role is for, in the application's own terms.">
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          style={inputStyle}
        />
      </Field>
    </Modal>
  );
}

/**
 * Pick app roles to assign, one application at a time.
 *
 * ── AN APPLICATION FIRST, THEN ITS ROLES ─────────────────────────────
 *
 * The alternative is one flat list of every role on the platform, which is the
 * shape the whole-set write actually takes. It is rejected because app-role
 * names are unique only WITHIN an application: a flat list shows `admin` three
 * times with nothing to tell them apart but a column, and picking the wrong one
 * assigns authority inside an application nobody meant to touch.
 *
 * It also means one request per application viewed rather than one per
 * application that exists, which matters on a platform that is meant to grow by
 * registering apps.
 */
export function AppRolePicker({
  applications,
  alreadyHeld,
  onClose,
  onConfirm,
}: {
  applications: { id: string; slug: string; name: string }[];
  alreadyHeld: string[];
  onClose: () => void;
  onConfirm: (picked: string[]) => void;
}) {
  const [appId, setAppId] = useState(applications[0]?.id ?? '');
  const [picked, setPicked] = useState<string[]>([]);
  const roles = useAsync(() => (appId ? adminApi.appRoles(appId) : Promise.resolve({ roles: [] })), [appId]);

  return (
    <Modal
      title="Assign an app role"
      onClose={onClose}
      footer={
        <ModalActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button disabled={picked.length === 0} onClick={() => onConfirm(picked)}>
            Assign
          </Button>
        </ModalActions>
      }
    >
      <p style={{ fontSize: '.88rem', color: C.muted, marginTop: 0 }}>
        The application decides what its roles mean. This platform stores the name and puts it in
        the token minted for that application, and nothing else.
      </p>

      <Field label="Application">
        <Select
          block
          label="Application"
          value={appId}
          onChange={(v) => {
            setAppId(v);
            // Cleared on switch: a tick made under one application must not be
            // carried into another, where the reader has no way to see it.
            setPicked([]);
          }}
          options={applications.map((a) => ({ value: a.id, label: `${a.name} (${a.slug})` }))}
        />
      </Field>

      <AsyncBoundary state={roles.state}>
        {(d) => {
          const available = d.roles.filter((r) => !alreadyHeld.includes(r.id));
          if (available.length === 0) {
            return (
              <p style={{ fontSize: '.88rem', color: C.muted, margin: 0 }}>
                {d.roles.length === 0
                  ? 'This application defines no roles yet. Create one on its own screen, under Security.'
                  : 'This account already holds every role this application defines.'}
              </p>
            );
          }
          return (
            <div style={{ display: 'grid', gap: '.5rem' }}>
              {available.map((r) => (
                <label
                  key={r.id}
                  style={{ display: 'flex', alignItems: 'center', gap: '.45rem', fontSize: '.88rem' }}
                >
                  <input
                    type="checkbox"
                    checked={picked.includes(r.id)}
                    onChange={() =>
                      setPicked(
                        picked.includes(r.id)
                          ? picked.filter((v) => v !== r.id)
                          : [...picked, r.id],
                      )
                    }
                  />
                  <span>
                    {r.label} <code style={{ color: C.muted }}>{r.name}</code>
                  </span>
                </label>
              ))}
            </div>
          );
        }}
      </AsyncBoundary>
    </Modal>
  );
}
